import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { OPS } from "../constants/testIds4";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, Ticket, Package, GraduationCap, Star, CheckCircle } from "@phosphor-icons/react";

const T_CATS = ["payroll", "leave", "it", "policy", "benefits", "other"];
const T_PRIO = ["low", "medium", "high"];
const T_STATUS = ["open", "in_progress", "resolved", "closed"];
const T_STATUS_STYLE = { open: "bg-amber-50 text-amber-800 border-amber-200", in_progress: "bg-blue-50 text-blue-800 border-blue-200", resolved: "bg-emerald-50 text-emerald-800 border-emerald-200", closed: "bg-zinc-100 text-zinc-700 border-zinc-300" };
const T_PRIO_STYLE = { low: "bg-zinc-50 border-zinc-200", medium: "bg-amber-50 text-amber-800 border-amber-200", high: "bg-red-50 text-red-800 border-red-200" };
const A_KINDS = ["laptop", "id_card", "sim", "monitor", "other"];

export default function Operations() {
  const { user, tenantId } = useAuth();
  const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Operations HR</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Helpdesk · Assets · Learning · Skills</h1>
        <p className="text-sm text-zinc-500 mt-2">Employee ticketing, asset lifecycle, courses &amp; enrollments, and a skills matrix at a glance.</p>
      </div>

      <Tabs defaultValue="tickets">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm flex-wrap h-auto">
          <TabsTrigger value="tickets" className="rounded-sm" data-testid={OPS.tabTickets}><Ticket size={14} className="mr-1" /> Helpdesk</TabsTrigger>
          <TabsTrigger value="assets" className="rounded-sm" data-testid={OPS.tabAssets}><Package size={14} className="mr-1" /> Assets</TabsTrigger>
          <TabsTrigger value="courses" className="rounded-sm" data-testid={OPS.tabCourses}><GraduationCap size={14} className="mr-1" /> L&amp;D</TabsTrigger>
          <TabsTrigger value="skills" className="rounded-sm" data-testid={OPS.tabSkills}><Star size={14} className="mr-1" /> Skills</TabsTrigger>
        </TabsList>

        <TabsContent value="tickets" className="mt-4"><TicketsPanel user={user} params={params} /></TabsContent>
        <TabsContent value="assets" className="mt-4"><AssetsPanel user={user} params={params} tenantId={tenantId} /></TabsContent>
        <TabsContent value="courses" className="mt-4"><CoursesPanel user={user} params={params} tenantId={tenantId} /></TabsContent>
        <TabsContent value="skills" className="mt-4"><SkillsPanel user={user} params={params} /></TabsContent>
      </Tabs>
    </div>
  );
}

// -------- Tickets --------
function TicketsPanel({ user, params }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "it", priority: "medium", description: "" });
  const isHR = ["super_admin", "company_admin", "hr", "manager"].includes(user?.role);

  const load = () => api.get("/tickets", { params }).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params.tenant_id]);

  const save = async () => {
    try { await api.post("/tickets", form); toast.success("Ticket created"); setOpen(false); setForm({ subject: "", category: "it", priority: "medium", description: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const update = async (id, patch) => { try { await api.patch(`/tickets/${id}`, patch); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-zenith rounded-sm" data-testid={OPS.ticketCreateBtn}><Plus size={16} className="mr-1.5" /> Raise ticket</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display text-2xl">New ticket</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1"><Label className="text-xs">Subject *</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-sm" data-testid="ticket-subject-input" /></div>
              <div className="space-y-1"><Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{T_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label className="text-xs">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{T_PRIO.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-1"><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-sm" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={save} className="btn-zenith rounded-sm" data-testid={OPS.ticketSaveBtn}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="hr-table">
          <thead><tr><th>Subject</th><th>By</th><th>Category</th><th>Priority</th><th>Status</th><th>Created</th>{isHR && <th></th>}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={isHR ? 7 : 6} className="text-center py-6 text-zinc-500 text-sm">No tickets.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id}>
                <td><div className="text-sm">{r.subject}</div>{r.description && <div className="text-xs text-zinc-500 truncate max-w-[300px]">{r.description}</div>}</td>
                <td className="text-xs">{r.raised_by_name}</td>
                <td className="capitalize text-xs">{r.category}</td>
                <td><Badge variant="outline" className={`rounded-sm ${T_PRIO_STYLE[r.priority]}`}>{r.priority}</Badge></td>
                <td><Badge variant="outline" className={`rounded-sm ${T_STATUS_STYLE[r.status]}`}>{r.status.replace("_", " ")}</Badge></td>
                <td className="font-mono-plex text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                {isHR && (
                  <td>
                    <Select value={r.status} onValueChange={(v) => update(r.id, { status: v })}>
                      <SelectTrigger className="rounded-sm h-7 text-xs w-[130px]" data-testid={`ticket-status-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{T_STATUS.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------- Assets --------
function AssetsPanel({ user, params, tenantId }) {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", kind: "laptop", serial_number: "", label: "", issued_on: new Date().toISOString().slice(0, 10), notes: "" });
  const [returnOpen, setReturnOpen] = useState(null);
  const [rd, setRd] = useState({ returned_on: new Date().toISOString().slice(0, 10), condition_notes: "" });
  const isHR = ["super_admin", "company_admin", "hr"].includes(user?.role);

  const load = () => api.get("/assets", { params }).then(({ data }) => setRows(data));
  useEffect(() => { load(); if (isHR) api.get("/employees", { params }).then(({ data }) => setEmployees(data)); /* eslint-disable-next-line */ }, [params.tenant_id]);

  const issue = async () => {
    try {
      const payload = { ...form };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/assets", payload); toast.success("Asset issued"); setOpen(false);
      setForm({ employee_id: "", kind: "laptop", serial_number: "", label: "", issued_on: new Date().toISOString().slice(0, 10), notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doReturn = async () => { try { await api.post(`/assets/${returnOpen}/return`, rd); toast.success("Returned"); setReturnOpen(null); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="space-y-3">
      {isHR && (
        <div className="flex justify-end">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="btn-zenith rounded-sm" data-testid={OPS.assetIssueBtn}><Plus size={16} className="mr-1.5" /> Issue asset</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl">Issue asset</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1"><Label className="text-xs">Employee *</Label>
                  <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="asset-emp-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} · {e.employee_code}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Kind</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>{A_KINDS.map((k) => <SelectItem key={k} value={k}>{k.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Serial #</Label><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} className="rounded-sm font-mono-plex" /></div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Label</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. Dell Latitude 5430" className="rounded-sm" data-testid="asset-label-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Issued on</Label><Input type="date" value={form.issued_on} onChange={(e) => setForm({ ...form, issued_on: e.target.value })} className="rounded-sm" /></div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
                <Button onClick={issue} className="btn-zenith rounded-sm" data-testid={OPS.assetSaveBtn}>Issue</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <div className="border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="hr-table">
          <thead><tr><th>Employee</th><th>Kind</th><th>Label</th><th>Serial</th><th>Issued</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-zinc-500 text-sm">No assets tracked.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id}>
                <td><div className="text-sm">{r.employee_name}</div><div className="text-xs font-mono-plex text-zinc-500">{r.employee_code}</div></td>
                <td className="capitalize">{r.kind.replace(/_/g, " ")}</td>
                <td>{r.label}</td>
                <td className="font-mono-plex text-xs">{r.serial_number || "—"}</td>
                <td className="font-mono-plex text-xs">{r.issued_on}</td>
                <td><Badge variant="outline" className={`rounded-sm ${r.status === "issued" ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-zinc-100 border-zinc-300"}`}>{r.status}</Badge></td>
                <td>{isHR && r.status === "issued" && <button onClick={() => setReturnOpen(r.id)} data-testid={OPS.assetReturnBtn(r.id)} className="text-xs px-2 py-1 border border-zinc-300 hover:bg-zinc-50 rounded-sm">Return</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!returnOpen} onOpenChange={(v) => !v && setReturnOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">Return asset</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs">Returned on</Label><Input type="date" value={rd.returned_on} onChange={(e) => setRd({ ...rd, returned_on: e.target.value })} className="rounded-sm" /></div>
            <div className="space-y-1"><Label className="text-xs">Condition notes</Label><Textarea value={rd.condition_notes} onChange={(e) => setRd({ ...rd, condition_notes: e.target.value })} className="rounded-sm" data-testid="asset-return-notes" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnOpen(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={doReturn} className="btn-zenith rounded-sm" data-testid="asset-return-save-btn">Mark returned</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- Courses & Enrollments --------
function CoursesPanel({ user, params, tenantId }) {
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [openC, setOpenC] = useState(false);
  const [openE, setOpenE] = useState(false);
  const [cf, setCf] = useState({ code: "", title: "", provider: "", duration_hours: 0, description: "" });
  const [ef, setEf] = useState({ course_id: "", employee_id: "", due_date: "" });
  const isHR = ["super_admin", "company_admin", "hr"].includes(user?.role);

  const load = () => {
    api.get("/courses", { params }).then(({ data }) => setCourses(data));
    api.get("/enrollments", { params }).then(({ data }) => setEnrollments(data));
    if (isHR) api.get("/employees", { params }).then(({ data }) => setEmployees(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [params.tenant_id]);

  const saveC = async () => {
    try {
      const payload = { ...cf, duration_hours: parseFloat(cf.duration_hours) || 0 };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/courses", payload); toast.success("Course added"); setOpenC(false);
      setCf({ code: "", title: "", provider: "", duration_hours: 0, description: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const saveE = async () => {
    try {
      const payload = { ...ef };
      if (!payload.due_date) delete payload.due_date;
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/enrollments", payload); toast.success("Enrolled"); setOpenE(false);
      setEf({ course_id: "", employee_id: "", due_date: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const complete = async (id) => { try { await api.post(`/enrollments/${id}/complete`); toast.success("Completed"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  const nameById = (id) => { const c = courses.find((x) => x.id === id); return c ? `${c.code} · ${c.title}` : id?.slice(-6); };

  return (
    <div className="space-y-4">
      {isHR && (
        <div className="flex justify-end gap-2">
          <Dialog open={openC} onOpenChange={setOpenC}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-sm" data-testid={OPS.courseCreateBtn}><Plus size={16} className="mr-1.5" /> Course</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl">New course</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Code *</Label><Input value={cf.code} onChange={(e) => setCf({ ...cf, code: e.target.value.toUpperCase() })} className="rounded-sm font-mono-plex" data-testid="course-code-input" /></div>
                <div className="space-y-1"><Label className="text-xs">Duration (hrs)</Label><Input type="number" step="0.5" value={cf.duration_hours} onChange={(e) => setCf({ ...cf, duration_hours: e.target.value })} className="rounded-sm font-mono-plex" /></div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Title *</Label><Input value={cf.title} onChange={(e) => setCf({ ...cf, title: e.target.value })} className="rounded-sm" data-testid="course-title-input" /></div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Provider</Label><Input value={cf.provider} onChange={(e) => setCf({ ...cf, provider: e.target.value })} className="rounded-sm" /></div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Description</Label><Textarea value={cf.description} onChange={(e) => setCf({ ...cf, description: e.target.value })} className="rounded-sm" /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpenC(false)} className="rounded-sm">Cancel</Button><Button onClick={saveC} className="btn-zenith rounded-sm" data-testid={OPS.courseSaveBtn}>Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Dialog open={openE} onOpenChange={setOpenE}>
            <DialogTrigger asChild>
              <Button className="btn-zenith rounded-sm" data-testid={OPS.enrollBtn}><Plus size={16} className="mr-1.5" /> Enroll</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-display text-2xl">Enroll employee</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1"><Label className="text-xs">Course *</Label>
                  <Select value={ef.course_id} onValueChange={(v) => setEf({ ...ef, course_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="enroll-course-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} · {c.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Employee *</Label>
                  <Select value={ef.employee_id} onValueChange={(v) => setEf({ ...ef, employee_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="enroll-emp-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-1"><Label className="text-xs">Due date</Label><Input type="date" value={ef.due_date} onChange={(e) => setEf({ ...ef, due_date: e.target.value })} className="rounded-sm" /></div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setOpenE(false)} className="rounded-sm">Cancel</Button><Button onClick={saveE} className="btn-zenith rounded-sm" data-testid="enroll-save-btn">Enroll</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1.5">Course catalogue</div>
          <div className="border border-zinc-200 rounded-sm overflow-x-auto">
            <table className="hr-table">
              <thead><tr><th>Code</th><th>Title</th><th>Provider</th><th>Hrs</th></tr></thead>
              <tbody>
                {courses.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-zinc-500 text-sm">No courses.</td></tr>}
                {courses.map((c) => (
                  <tr key={c.id}>
                    <td className="font-mono-plex text-xs">{c.code}</td>
                    <td>{c.title}</td>
                    <td className="text-xs">{c.provider || "—"}</td>
                    <td className="font-mono-plex text-xs">{c.duration_hours}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1.5">Enrollments</div>
          <div className="border border-zinc-200 rounded-sm overflow-x-auto">
            <table className="hr-table">
              <thead><tr><th>Course</th><th>Employee</th><th>Due</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {enrollments.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-zinc-500 text-sm">No enrollments.</td></tr>}
                {enrollments.map((e) => (
                  <tr key={e.id}>
                    <td className="text-xs">{nameById(e.course_id)}</td>
                    <td className="text-xs font-mono-plex">{e.employee_id?.slice(-6)}</td>
                    <td className="font-mono-plex text-xs">{e.due_date || "—"}</td>
                    <td><Badge variant="outline" className={`rounded-sm ${e.status === "completed" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-blue-50 text-blue-800 border-blue-200"}`}>{e.status}</Badge></td>
                    <td>{e.status !== "completed" && (user?.role !== "employee" || e.employee_id === user?.employee_id) && <button onClick={() => complete(e.id)} data-testid={OPS.completeBtn(e.id)} className="text-xs px-2 py-1 border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm"><CheckCircle size={12} className="inline mr-1" /> Complete</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// -------- Skills matrix --------
function SkillsPanel({ user, params }) {
  const [matrix, setMatrix] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", skill: "", level: 3 });
  const isHR = ["super_admin", "company_admin", "hr", "manager"].includes(user?.role);

  const load = () => api.get("/skills/matrix", { params }).then(({ data }) => setMatrix(data));
  useEffect(() => { load(); if (isHR) api.get("/employees", { params }).then(({ data }) => setEmployees(data)); /* eslint-disable-next-line */ }, [params.tenant_id]);

  const save = async () => {
    try { await api.post("/skills", { ...form, level: parseInt(form.level) }); toast.success("Saved"); setOpen(false); setForm({ employee_id: "", skill: "", level: 3 }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-zenith rounded-sm" data-testid={OPS.skillAddBtn}><Plus size={16} className="mr-1.5" /> Add / update skill</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display text-2xl">Skill</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1"><Label className="text-xs">Employee *</Label>
                <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="skill-emp-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {user?.employee_id && <SelectItem value={user.employee_id}>Myself</SelectItem>}
                    {isHR && employees.filter((e) => e.id !== user?.employee_id).map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-1"><Label className="text-xs">Skill *</Label><Input value={form.skill} onChange={(e) => setForm({ ...form, skill: e.target.value })} className="rounded-sm" placeholder="e.g. Python, Kubernetes, Excel" data-testid="skill-name-input" /></div>
              <div className="col-span-2 space-y-1"><Label className="text-xs">Level (1-5)</Label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} onClick={() => setForm({ ...form, level: n })} data-testid={`skill-lvl-${n}`} className={`w-10 h-10 border rounded-sm ${form.level === n ? "bg-emerald-700 text-white border-emerald-800" : "bg-white border-zinc-200"}`}>{n}</button>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={save} className="btn-zenith rounded-sm" data-testid="skill-save-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-zinc-200 rounded-sm overflow-x-auto">
        <table className="hr-table">
          <thead><tr><th>Employee</th><th>Department</th><th>Skills</th></tr></thead>
          <tbody>
            {matrix.length === 0 && <tr><td colSpan={3} className="text-center py-6 text-zinc-500 text-sm">No skills recorded yet.</td></tr>}
            {matrix.map((r) => (
              <tr key={r.employee_id}>
                <td><div className="text-sm">{r.employee_name}</div><div className="text-xs font-mono-plex text-zinc-500">{r.employee_code}</div></td>
                <td className="text-xs">{r.department || "—"}</td>
                <td>
                  <div className="flex flex-wrap gap-1.5">
                    {r.skills.map((s, i) => (
                      <div key={i} className="border border-zinc-200 rounded-sm px-2 py-1 flex items-center gap-1.5 bg-zinc-50">
                        <span className="text-xs">{s.skill}</span>
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={10} weight={n <= s.level ? "fill" : "regular"} className={n <= s.level ? "text-emerald-700" : "text-zinc-300"} />)}
                        </div>
                      </div>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
